/**
 * Holiday Mapper
 *
 * Pure transforms from NBC API holiday data to UI SelectOptions.
 * Each NBCHoliday's `periods` array is expanded into separate options.
 *
 * @module services/mappers/holidayMapper
 */

import type { SelectOption } from '@/components/ui/BaseSelect.vue';
import type { DateRange } from '@/types/SearchBar';
import type { NBCState, NBCHoliday } from '@/types/schemas/nbc/holidays';
import { $t } from '@/composables/translations/useTranslate';

export type HolidayOptionLookup = Map<string, DateRange>;

export interface MappedHolidayOptions {
  options: SelectOption[];
  lookup: HolidayOptionLookup;
}

export function mapStatesToOptions(states: NBCState[]): SelectOption[] {
  return states.map((state) => ({ value: state.slug, label: state.name }));
}

/**
 * Parse ISO date string at local midnight to avoid timezone shift.
 *
 * `new Date("2026-07-16")` is parsed as UTC midnight, which in negative-offset
 * timezones rolls back to the previous day. Appending the time forces local midnight.
 */
function parseLocalDate(iso: string): Date {
  return new Date(`${iso}T00:00:00`);
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function formatDayMonth(date: Date): string {
  return `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}.`;
}

function formatDayMonthYear(date: Date): string {
  return `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}.${date.getFullYear()}`;
}

function formatPeriodRange(start: Date, end: Date): string {
  if (start.getTime() === end.getTime()) {
    return formatDayMonthYear(start);
  }
  if (start.getFullYear() !== end.getFullYear()) {
    return `${formatDayMonthYear(start)} – ${formatDayMonthYear(end)}`;
  }
  return `${formatDayMonth(start)} – ${formatDayMonthYear(end)}`;
}

/**
 * Build a stable option `value` key from holiday name + period dates.
 * Format: `${name}::${start}::${end}`.
 * Both ISO dates guarantee uniqueness across years.
 */
function buildOptionKey(holidayName: string, period: { start: string; end: string }): string {
  return `${holidayName}::${period.start}::${period.end}`;
}

export function mapHolidaysToOptions(holidays: NBCHoliday[]): MappedHolidayOptions {
  const options: SelectOption[] = [];
  const lookup: HolidayOptionLookup = new Map();
  const currentYear = new Date().getFullYear();

  for (const holiday of holidays) {
    for (const period of holiday.periods) {
      const start = parseLocalDate(period.start);
      const end = parseLocalDate(period.end);
      const key = buildOptionKey(holiday.name, period);
      const range = formatPeriodRange(start, end);
      const name =
        start.getFullYear() > currentYear ? `${holiday.name} ${start.getFullYear()}` : holiday.name;
      const label = $t('ibe.holiday.period-label', '{name} ({range})', {
        name,
        range,
      });

      options.push({ value: key, label });
      lookup.set(key, [start, end]);
    }
  }

  return { options, lookup };
}
